import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { useAuthToken } from '@/hooks/useAuthToken';
import { supabaseApi } from '@/lib/api/supabase';
import { SupabaseStatus } from '@optidev/shared-types';
import { ActivationPanel } from './ActivationPanel';
import { CloudManagement } from './CloudManagement';
import { useResponsive } from '@/hooks/useResponsive';
import { cn } from '@/lib/utils';
import toast from 'react-hot-toast';

interface OptiDevCloudProps {
  projectId?: string;
}

export default function OptiDevCloud({ projectId: propProjectId }: OptiDevCloudProps = {}) {
  const { projectId: routeProjectId } = useParams<{ projectId: string }>();
  const projectId = propProjectId || routeProjectId;
  const { token } = useAuthToken();
  const { isMobile } = useResponsive();
  const [isActivated, setIsActivated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<SupabaseStatus | null>(null);

  useEffect(() => {
    if (token) {
      checkActivationStatus();
    }
  }, [projectId, token]);

  const checkActivationStatus = async () => {
    if (!token || !projectId) return;

    try {
      const statusData = await supabaseApi.getProjectStatus(token, projectId);
      setStatus(statusData.status);
      setIsActivated(statusData.isActivated);
    } catch (error: unknown) {
      const err = error as { response?: { status?: number } };
      if (err.response?.status === 400) {
        setIsActivated(false);
        setStatus(SupabaseStatus.INACTIVE);
      } else {
        console.error('Failed to check activation status:', error);
        toast.error('Failed to check activation status');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleActivated = () => {
    setIsActivated(true);
    checkActivationStatus();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-sm text-muted-foreground">Loading...</div>
      </div>
    );
  }

  // Not activated - show activation panel centered
  if (!isActivated || status === SupabaseStatus.PROVISIONING) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <ActivationPanel
          projectId={projectId!}
          onActivated={handleActivated}
          initialStatus={status}
        />
      </div>
    );
  }

  // Activated - show management UI
  return (
    <div className={cn("h-full flex flex-col", isMobile ? "" : "")}>
      <CloudManagement projectId={projectId!} status={status!} />
    </div>
  );
}
